import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { authFetch } from "../../App";
import { ArrowLeft, FileText, ShieldAlert, CheckCircle2, AlertTriangle } from "lucide-react";

const API = "/api";

export default function HpsBericht() {
  const { reportId } = useParams();
  const [report, setReport] = useState(null);
  const [inspectionFull, setInspectionFull] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const reps = await authFetch(`${API}/hps/reports`).then(r => r.json());
        const r = (Array.isArray(reps) ? reps : []).find((x) => x.id === reportId);
        setReport(r || null);
        if (r?.inspection_id) {
          const full = await authFetch(`${API}/hps/inspections/${r.inspection_id}/full`).then(x => x.json());
          setInspectionFull(full);
        }
      } finally { setLoading(false); }
    })();
  }, [reportId]);

  if (loading) return <div className="text-white/60 text-sm py-20 text-center">Lade Bericht …</div>;
  if (!report) return <div className="text-red-300 text-sm">Bericht nicht gefunden</div>;

  const customer = inspectionFull?.customer;
  const object = inspectionFull?.object;
  const inspection = inspectionFull?.inspection;
  const defects = inspectionFull?.defects || [];
  const proposals = inspectionFull?.proposals || [];

  const counts = defects.reduce((a, d) => { a[d.priority] = (a[d.priority]||0)+1; return a; }, {});

  return (
    <div className="space-y-6" data-testid="hps-bericht">
      <Link to={`/app/hps-anlagencheck/pruefung/${report.inspection_id}`}
            className="inline-flex items-center gap-1 text-xs text-white/55 hover:text-white">
        <ArrowLeft className="w-3 h-3" /> zurück zur Prüfung
      </Link>

      {/* Cover */}
      <div className="rounded-2xl border border-white/10 p-6 md:p-8"
           style={{ background: "linear-gradient(135deg, rgba(74,158,255,0.10), rgba(37,99,235,0.04))" }}>
        <div className="flex items-center gap-2 mb-4 text-xs text-blue-200">
          <FileText className="w-4 h-4" />
          <span>PRÜFBERICHT</span>
          {report.is_demo && <span className="text-[10px] uppercase tracking-wider text-amber-300 bg-amber-500/15 border border-amber-400/30 rounded px-1.5 py-0.5">Demo</span>}
        </div>
        <h1 className="text-xl md:text-3xl font-semibold text-white leading-tight">{report.title}</h1>
        <div className="mt-3 text-sm text-white/70">
          {customer?.name} · {object?.name}
        </div>
        <div className="mt-1 text-xs text-white/50">
          Prüfdatum: {inspection?.inspection_date} · Prüfer: {inspection?.inspector_name || "—"}
        </div>
        <div className="mt-4 flex items-center gap-2">
          <span className="text-[11px] uppercase tracking-wider text-white/50">Status:</span>
          <span className="text-xs text-amber-200 bg-amber-500/15 border border-amber-400/30 rounded px-2 py-1 flex items-center gap-1">
            <AlertTriangle className="w-3 h-3" /> Entwurf / fachlich zu prüfen
          </span>
        </div>
      </div>

      {/* Zusammenfassung */}
      <Section title="Zusammenfassung">
        <p className="text-sm text-white/85 leading-relaxed whitespace-pre-wrap">{report.summary}</p>
      </Section>

      {/* Prioritätenampel */}
      <Section title="Prioritätenampel">
        <div className="grid grid-cols-3 gap-3">
          <Tile color="#ef4444" label="Hoch / Kritisch" count={(counts.hoch||0)+(counts.kritisch||0)} />
          <Tile color="#f59e0b" label="Mittel" count={counts.mittel||0} />
          <Tile color="#22c55e" label="Niedrig" count={counts.niedrig||0} />
        </div>
      </Section>

      {/* Mängelliste */}
      <Section title={`Mängelliste (${defects.length})`}>
        <div className="space-y-2">
          {defects.map((d) => (
            <div key={d.id} className="rounded-lg border border-white/10 p-3 flex items-start gap-2"
                 style={{ background: "rgba(255,255,255,0.025)" }}>
              <span className="text-[10px] uppercase tracking-wider rounded px-1.5 py-0.5 border bg-white/5 text-white/70 border-white/10 shrink-0">
                {d.priority}
              </span>
              <div className="text-sm text-white/85">{d.title}</div>
            </div>
          ))}
        </div>
      </Section>

      {/* Sanierungsempfehlungen */}
      <Section title={`Sanierungsempfehlungen (${proposals.length})`}>
        <div className="space-y-2">
          {proposals.map((p) => (
            <div key={p.id} className="rounded-lg border border-emerald-400/20 p-3"
                 style={{ background: "rgba(34,197,94,0.04)" }}>
              <div className="text-sm text-white font-medium flex items-center gap-2">
                <CheckCircle2 className="w-4 h-4 text-emerald-300" /> {p.title}
              </div>
              {p.description && <div className="text-xs text-white/65 mt-1 pl-6 leading-relaxed">{p.description}</div>}
              {p.estimated_cost_range && (
                <div className="text-xs text-emerald-300 mt-2 pl-6 font-medium">{p.estimated_cost_range}</div>
              )}
            </div>
          ))}
        </div>
      </Section>

      <div className="rounded-lg border border-amber-400/20 bg-amber-500/10 p-3 text-xs text-amber-100/90 flex items-start gap-2">
        <ShieldAlert className="w-3.5 h-3.5 mt-0.5 shrink-0" />
        <span>
          Die KI unterstützt Bewertung und Dokumentation. Die finale fachliche Freigabe erfolgt durch eine verantwortliche Elektrofachkraft oder HPS.
        </span>
      </div>
    </div>
  );
}


function Section({ title, children }) {
  return (
    <section className="rounded-xl border border-white/10 p-5"
             style={{ background: "rgba(255,255,255,0.04)" }}>
      <h2 className="text-sm font-semibold text-white mb-3 uppercase tracking-wider">{title}</h2>
      {children}
    </section>
  );
}

function Tile({ color, label, count }) {
  return (
    <div className="rounded-lg border border-white/10 p-4 text-center"
         style={{ background: "rgba(255,255,255,0.025)" }}>
      <div className="w-3 h-3 rounded-full mx-auto mb-2" style={{ background: color }} />
      <div className="text-xl font-semibold text-white">{count}</div>
      <div className="text-[10px] uppercase tracking-wider text-white/50 mt-1">{label}</div>
    </div>
  );
}
